import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FormFormat,
  FormKind,
  FormSummary,
  GenerateDischargeCertificateDto,
  GenerateFormDto,
  GenerateNoticeToComplyDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';
import { form4OverrideKeys } from './form-overrides-api';

export async function listForms(fileId: string): Promise<FormSummary[]> {
  const { data } = await api.get<FormSummary[]>(`/files/${fileId}/forms`);
  return data;
}

export async function generateNoticeToComply(
  fileId: string,
  dto: GenerateNoticeToComplyDto,
): Promise<FormSummary> {
  const { data } = await api.post<FormSummary>(`/files/${fileId}/forms/notice-to-comply`, dto);
  return data;
}

export async function generateDischargeCertificate(
  fileId: string,
  dto: GenerateDischargeCertificateDto,
): Promise<FormSummary> {
  const { data } = await api.post<FormSummary>(`/files/${fileId}/forms/discharge-certificate`, dto);
  return data;
}

export async function generateForm(
  fileId: string,
  kind: FormKind,
  dto: GenerateFormDto = {},
  format: FormFormat = 'docx',
): Promise<FormSummary> {
  const { data } = await api.post<FormSummary>(`/files/${fileId}/forms/${kind}`, dto, {
    params: { format },
  });
  return data;
}

/**
 * Open a document for authoring instead of issuing it outright. The draft is a
 * working copy: it ticks no checklist item and reaches nobody until it is
 * published.
 */
export async function createDraft(
  fileId: string,
  kind: FormKind,
  dto: GenerateFormDto = {},
): Promise<FormSummary> {
  const { data } = await api.post<FormSummary>(`/files/${fileId}/forms/drafts/${kind}`, dto);
  return data;
}

/**
 * Hand the draft's .docx to the officer to edit in Word.
 *
 * Fetched through the API client rather than linked to, because the route is
 * cookie-authenticated and a plain `<a href>` to it would not carry the session
 * on every browser. The blob is turned into a download client-side.
 */
export async function downloadDraft(
  fileId: string,
  formId: string,
  filename: string,
): Promise<void> {
  const { data } = await api.get<Blob>(`/files/${fileId}/forms/${formId}/content`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking immediately would race the download on some browsers; one tick is
  // enough for the click to have been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Put the officer's edited .docx back over the draft's working copy. */
export async function uploadDraft(
  fileId: string,
  formId: string,
  file: File,
  // The draft's `updatedAt` when the officer took it to edit. If someone saved
  // over it since, the API refuses this copy instead of overwriting theirs.
  expectedUpdatedAt?: string,
): Promise<FormSummary> {
  const body = new FormData();
  body.append('document', file);
  if (expectedUpdatedAt) body.append('expectedUpdatedAt', expectedUpdatedAt);
  const { data } = await api.put<FormSummary>(`/files/${fileId}/forms/${formId}/content`, body);
  return data;
}

/** Freeze the draft — the PDF produced here is the operative document. */
export async function publishForm(fileId: string, formId: string): Promise<FormSummary> {
  const { data } = await api.post<FormSummary>(`/files/${fileId}/forms/${formId}/publish`);
  return data;
}

/** The recreate button: a new draft carrying the published version's text. */
export async function reviseForm(fileId: string, formId: string): Promise<FormSummary> {
  const { data } = await api.post<FormSummary>(`/files/${fileId}/forms/${formId}/revise`);
  return data;
}

export const formsKeys = {
  forFile: (fileId: string) => ['forms', fileId] as const,
};

/**
 * The authoring actions, which all reduce to "change this document's state, then
 * refetch the file's documents".
 *
 * Publishing also invalidates the Step 5.1 override log: publication is what
 * writes it now, since that is the moment the notice issues.
 */
function useFormLifecycleMutation<TVars>(
  fileId: string | undefined,
  mutationFn: (vars: TVars) => Promise<FormSummary>,
  successKey: string,
  errorKey: string,
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (!fileId) return;
      qc.invalidateQueries({ queryKey: formsKeys.forFile(fileId) });
      qc.invalidateQueries({ queryKey: form4OverrideKeys.byFile(fileId) });
      toast.success(successKey);
    },
    onError: (err) => toast.error(err, errorKey),
  });
}

export function useCreateDraft(fileId: string | undefined) {
  return useFormLifecycleMutation(
    fileId,
    ({ kind, dto }: { kind: FormKind; dto?: GenerateFormDto }) =>
      createDraft(fileId as string, kind, dto),
    'gov.forms.draftStartedToast',
    'gov.forms.draftError',
  );
}

export function useUploadDraft(fileId: string | undefined) {
  return useFormLifecycleMutation(
    fileId,
    ({
      formId,
      file,
      expectedUpdatedAt,
    }: {
      formId: string;
      file: File;
      expectedUpdatedAt?: string;
    }) => uploadDraft(fileId as string, formId, file, expectedUpdatedAt),
    'gov.forms.uploadedToast',
    'gov.forms.uploadError',
  );
}

export function usePublishForm(fileId: string | undefined) {
  return useFormLifecycleMutation(
    fileId,
    ({ formId }: { formId: string }) => publishForm(fileId as string, formId),
    'gov.forms.publishedToast',
    'gov.forms.publishError',
  );
}

export function useReviseForm(fileId: string | undefined) {
  return useFormLifecycleMutation(
    fileId,
    ({ formId }: { formId: string }) => reviseForm(fileId as string, formId),
    'gov.forms.revisedToast',
    'gov.forms.reviseError',
  );
}

/** Generate any document kind for a file (generic endpoint), in Word or PDF. */
export function useGenerateForm(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: ({
      kind,
      dto,
      format,
    }: {
      kind: FormKind;
      dto?: GenerateFormDto;
      format?: FormFormat;
    }) => generateForm(fileId as string, kind, dto, format),
    onSuccess: () => {
      if (fileId) qc.invalidateQueries({ queryKey: formsKeys.forFile(fileId) });
      toast.success('gov.documents.generatedToast');
    },
    onError: (err) => toast.error(err, 'gov.documents.generateError'),
  });
}

export function useForms(fileId: string | undefined) {
  return useQuery({
    queryKey: formsKeys.forFile(fileId ?? ''),
    queryFn: () => listForms(fileId as string),
    enabled: !!fileId,
  });
}

export function useGenerateNoticeToComply(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (dto: GenerateNoticeToComplyDto) => generateNoticeToComply(fileId as string, dto),
    onError: (err) => toast.error(err, 'gov.documents.generateError'),
    onSuccess: () => {
      toast.success('gov.documents.generatedToast');
      if (!fileId) return;
      qc.invalidateQueries({ queryKey: formsKeys.forFile(fileId) });
      // Generating Form 4 is what writes the Step 5.1 override log, so the log
      // shown beside the field sheet has to refetch.
      qc.invalidateQueries({ queryKey: form4OverrideKeys.byFile(fileId) });
    },
  });
}

export function useGenerateDischargeCertificate(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (dto: GenerateDischargeCertificateDto) =>
      generateDischargeCertificate(fileId as string, dto),
    onSuccess: () => {
      if (fileId) qc.invalidateQueries({ queryKey: formsKeys.forFile(fileId) });
      toast.success('gov.documents.generatedToast');
    },
    onError: (err) => toast.error(err, 'gov.documents.generateError'),
  });
}
